import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";

import { useStore } from "@store/store";
import Header from "@components/header";
import Loader from "@components/loader";
import PopupDialog from "@components/popupDialog";
import { routes, coverNoteStatus } from "@utils/constant";
import {
  FETCH_INVOICE_COVER_NOTE,
  FETCH_INVOICE_COVER_NOTE_SUCCESS,
  FETCH_INVOICE_COVER_NOTE_FAILURE,
} from "@utils/actionTypes";
import API from "@services/axios";
import { invoiceDetailStyle } from "./style";
import {
  formatAustralianCurrency,
  stripTrailingZeros,
} from "@utils/commonFunctions";

function InvoiceCoverNote() {
  const classes = invoiceDetailStyle();
  const [state, dispatch] = useStore();
  const [data, setData] = useState({});
  const [openPopup, setOpenPopup] = useState(false);
  const { id } = useParams();
  const history = useHistory();

  let getInvoiceDetail = () => {
    dispatch({ type: FETCH_INVOICE_COVER_NOTE });
    API.get(`invoices/${id}/previewCoverNote`)
      .then(response => {
        if (Object.keys(response.data.data?.coverNoteData || {}).length > 0) {
          setData(response.data.data?.coverNoteData);
        }
        if ([coverNoteStatus.LEGACY_DATA, coverNoteStatus.RECONCILIATION_MISMATCH].includes(response.data.data?.coverNoteStatus)) {
          setOpenPopup(true);
        }
        dispatch({
          type: FETCH_INVOICE_COVER_NOTE_SUCCESS,
          payload: response.data.data,
        });
      })
      .catch(error => {
        dispatch({
          type: FETCH_INVOICE_COVER_NOTE_FAILURE,
          payload: error,
        });
        if (error?.response?.status === 404) {
          history.push(routes.pageNotFound);
        }
      });
  };
  useEffect(() => {
    getInvoiceDetail();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const showCoverNote = () => (
    <div className="table-responsive">
      <table>
        <thead>
          <tr>
            <th>Description</th>
            <th>{data.headerTitle}</th>
            <th>Total (AUD)</th>
          </tr>
        </thead>
        <tbody>
          {data?.charges?.kg?.hasData && (
            <>
              <tr>
                <td className="bold-text" colSpan={3}>
                  {data?.charges?.kg?.title}
                </td>
              </tr>
              {Object.keys(data?.charges?.kg?.kgCharges).map((itm, indx) => {
                return (
                  <tr key={`${itm}-${indx}`}>
                    <td className="capitalize">{itm}</td>
                    <td>
                      {data?.charges?.kg?.kgCharges[itm]?.weight
                        ? stripTrailingZeros(
                            parseFloat(
                              data?.charges?.kg?.kgCharges[itm]?.weight
                            ).toFixed(2)
                          ) +
                          (data?.charges?.kg?.kgCharges[itm]?.weight > 1
                            ? " kgs"
                            : " kg")
                        : "-"}
                    </td>
                    <td>
                      {stripTrailingZeros(
                        formatAustralianCurrency(
                          data?.charges?.kg?.kgCharges[itm]?.net
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </>
          )}

          {data?.charges?.uld?.hasData && (
            <>
              <tr>
                <td className="bold-text" colSpan={3}>
                  {data?.charges?.uld?.title}
                </td>
              </tr>
              {Object.keys(data?.charges?.uld?.uldCharges).map((itm, indx) => {
                return (
                  <tr key={`${itm}-${indx}`}>
                    <td className="capitalize">{itm}</td>
                    <td>
                      {data?.charges?.uld?.uldCharges[itm]?.quantity
                        ? data?.charges?.uld?.uldCharges[itm]?.quantity +
                          (data?.charges?.uld?.uldCharges[itm]?.quantity > 1
                            ? " units"
                            : " unit")
                        : "-"}
                    </td>
                    <td>
                      {stripTrailingZeros(
                        formatAustralianCurrency(
                          data?.charges?.uld?.uldCharges[itm]?.net
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </>
          )}

          {data?.charges?.hourly?.hasData && (
            <>
              <tr>
                <td className="bold-text" colSpan={3}>
                  {data?.charges?.hourly?.title}
                </td>
              </tr>
              {Object.keys(data?.charges?.hourly?.hourCharges).map(
                (itm, indx) => {
                  return (
                    <tr key={`${itm}-${indx}`}>
                      <td className="capitalize">{itm}</td>
                      <td>
                        {data?.charges?.hourly?.hourCharges[itm]?.time
                          ? data?.charges?.hourly?.hourCharges[itm]?.time +
                            (data?.charges?.hourly?.hourCharges[itm]?.time > 1
                              ? " hrs"
                              : " hr")
                          : "-"}
                      </td>
                      <td>
                        {stripTrailingZeros(
                          formatAustralianCurrency(
                            data?.charges?.hourly?.hourCharges[itm]?.net
                          )
                        )}
                      </td>
                    </tr>
                  );
                }
              )}
            </>
          )}
        </tbody>
        <tbody className="group">
          <tr>
            <td className="bold-text" colSpan={3}>
              Net fuel surcharge
            </td>
          </tr>
          <tr>
            <td>{`Fuel surcharge (${data?.fuelSurcharge?.fuelSurchargeRates?.map(num => num + "%").join(", ")})`}</td>
            <td>-</td>
            <td>
              {stripTrailingZeros(
                formatAustralianCurrency(data?.fuelSurcharge?.total)
              )}
            </td>
          </tr>
          {data?.mismatchFigure?.fuelSurcharge > 0 ? (
            <tr>
              <td className="" colSpan={2}>
                <p></p>
              </td>
              <td className="mismatch-figure">
                <p>
                  Original invoice:{" "}
                  <span className="bold-text">
                    {stripTrailingZeros(
                      formatAustralianCurrency(
                        data.mismatchFigure.fuelSurcharge
                      )
                    )}
                  </span>
                </p>
              </td>
            </tr>
          ) : (
            <></>
          )}

          <tr>
            <td className="bold-text" colSpan={3}>
              Toll charges
            </td>
          </tr>
          <tr>
            <td>Toll charges</td>
            <td>-</td>
            <td>
              {stripTrailingZeros(
                formatAustralianCurrency(data?.tollCharges?.total)
              )}
            </td>
          </tr>
          {data?.mismatchFigure?.tollCharge > 0 ? (
            <tr>
              <td className="" colSpan={2}>
                <p></p>
              </td>
              <td className="mismatch-figure">
                <p>
                  Original invoice:{" "}
                  <span className="bold-text">
                    {stripTrailingZeros(
                      formatAustralianCurrency(data.mismatchFigure.tollCharge)
                    )}
                  </span>
                </p>
              </td>
            </tr>
          ) : (
            <></>
          )}

          <tr>
            <td className="bold-text" colSpan={3}>
              {data?.additionalCharges?.title}
            </td>
          </tr>
          {data?.additionalCharges?.hasData && (
            <>
              {Object.keys(data?.additionalCharges?.additionalChargesList).map(
                (itm, indx) => {
                  return (
                    Number(
                      data?.additionalCharges?.additionalChargesList[itm]?.net
                    ) !== 0 && (
                      <tr key={`${itm}-${indx}`}>
                        <td>{itm}</td>
                        <td>-</td>
                        <td>
                          {stripTrailingZeros(
                            formatAustralianCurrency(
                              data?.additionalCharges?.additionalChargesList[
                                itm
                              ]?.net
                            )
                          )}
                        </td>
                      </tr>
                    )
                  );
                }
              )}
            </>
          )}
          {data?.mismatchFigure?.additionalCharge > 0 ? (
            <tr>
              <td className="" colSpan={2}>
                <p></p>
              </td>
              <td className="mismatch-figure">
                <p>
                  Original invoice:{" "}
                  <span className="bold-text">
                    {stripTrailingZeros(
                      formatAustralianCurrency(
                        data.mismatchFigure.additionalCharge
                      )
                    )}
                  </span>
                </p>
              </td>
            </tr>
          ) : (
            <></>
          )}
        </tbody>
        {data?.charges?.flat?.hasData && (
          <tbody className="group">
            {Object.keys(data?.charges?.flat?.flatCharges).map((itm, indx) => {
              return (
                <tr key={`${itm}`}>
                  <td className="capitalize">Job Quote</td>
                  <td>
                    {data?.charges?.flat?.flatCharges[itm]?.isAdhocJob
                      ? "Adhoc Job"
                      : "Future-dated Job"}
                  </td>
                  <td>
                    {stripTrailingZeros(
                      formatAustralianCurrency(
                        data?.charges?.flat?.flatCharges[itm]?.net
                      )
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        )}
        <tbody className="group">
          {data?.diffAmount?.hasData && (
            <>
              <tr>
                <td className="bold-text" colSpan={3}>
                  {data?.diffAmount?.title}
                </td>
              </tr>
              {Object.keys(data?.diffAmount?.diffAmountList).map(
                (itm, indx) => {
                  return (
                    <tr key={`${itm}-${indx}`}>
                      {itm === "dailyMinHrsSemi" && (
                        <td colSpan={2}>
                          <p>
                            Diff amount due to daily min hrs of Semi
                            <span className="p-txt">
                              {" "}
                              (inc. fuel surcharge){" "}
                            </span>
                          </p>
                        </td>
                      )}
                      {itm === "dailyMinHrsRigid" && (
                        <td colSpan={2}>
                          <p>
                            Diff amount due to daily min hrs of Rigid
                            <span className="p-txt">
                              {" "}
                              (inc. fuel surcharge){" "}
                            </span>
                          </p>
                        </td>
                      )}
                      {itm === "dailyMinHrsSemiConsignment" && (
                        <td colSpan={2}>
                          <p>
                            Diff amount due to daily consignment min hrs of Semi
                            <span className="p-txt">
                              {" "}
                              (inc. fuel surcharge){" "}
                            </span>
                          </p>
                        </td>
                      )}
                      {itm === "dailyMinHrsRigidConsignment" && (
                        <td colSpan={2}>
                          <p>
                            Diff amount due to daily consignment min hrs of
                            Rigid
                            <span className="p-txt">
                              {" "}
                              (inc. fuel surcharge){" "}
                            </span>
                          </p>
                        </td>
                      )}
                      <td>
                        {stripTrailingZeros(
                          formatAustralianCurrency(
                            data?.diffAmount?.diffAmountList[itm]?.net
                          )
                        )}
                      </td>
                    </tr>
                  );
                }
              )}
            </>
          )}

          <tr>
            <td className="bold-text" colSpan={2}>
              Net charge
            </td>
            <td className="bold-text">{formatAustralianCurrency(data?.net)}</td>
          </tr>
          {data?.mismatchFigure?.net > 0 ? (
            <tr>
              <td className="" colSpan={2}>
                <p></p>
              </td>
              <td className="mismatch-figure">
                <p>
                  Original invoice:{" "}
                  <span className="bold-text">
                    {formatAustralianCurrency(data.mismatchFigure.net)}
                  </span>
                </p>
              </td>
            </tr>
          ) : (
            <></>
          )}
          <tr>
            <td colSpan={2}>{`GST (${data?.taxRate || 0}%)`}</td>
            <td>{formatAustralianCurrency(data?.tax)}</td>
          </tr>
          {data?.mismatchFigure?.tax > 0 ? (
            <tr>
              <td className="" colSpan={2}>
                <p></p>
              </td>
              <td className="mismatch-figure">
                <p>
                  Original invoice:{" "}
                  <span className="bold-text">
                    {formatAustralianCurrency(data.mismatchFigure.tax)}
                  </span>
                </p>
              </td>
            </tr>
          ) : (
            <></>
          )}
          <tr>
            <td className="bold-text" colSpan={2}>
              Total invoice
            </td>
            <td className="bold-text">
              {formatAustralianCurrency(data?.gross)}
            </td>
          </tr>
          {data?.mismatchFigure?.gross > 0 ? (
            <tr>
              <td className="" colSpan={2}>
                <p></p>
              </td>
              <td className="mismatch-figure">
                <p>
                  Original invoice:{" "}
                  <span className="bold-text">
                    {formatAustralianCurrency(data.mismatchFigure.gross)}
                  </span>
                </p>
              </td>
            </tr>
          ) : (
            <></>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <Header />
      <div className={classes.invoiceDetailWrapper}>
        <Loader loading={state.invoice?.loadingInvoiceCoverNote} />
        <div className="invoice-detail-page wrapper">
          <div className="inner-page">
            <div className="invoice-detail-wrapper">
              <div className="content-wrapper">
                <div className="header">
                  <div className="top-header clearfix">
                    <div className="logo">
                      <img
                        src={state?.invoice?.invoiceCoverNoteData?.header?.logo}
                        alt="logo"
                      />
                    </div>
                    <div className="middle-title">
                      <h1>Cover Note</h1>
                      <p>
                        {state?.invoice?.invoiceCoverNoteData?.header
                          ?.invoiceNumber
                          ? state?.invoice?.invoiceCoverNoteData?.header
                              ?.invoiceNumber
                          : "TBA"}
                      </p>
                    </div>
                    <div className="right-block">
                      <p>
                        <span>Issue Date:</span>
                        <span>
                          {
                            state?.invoice?.invoiceCoverNoteData?.header
                              ?.issueDate
                          }
                        </span>
                      </p>
                      <p>
                        <span>Date Payable:</span>
                        <span>
                          {
                            state?.invoice?.invoiceCoverNoteData?.header
                              ?.payableDate
                          }
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="bottom-header clearfix">
                    <div className="left-block">
                      <p>
                        {state?.invoice?.invoiceCoverNoteData?.organisationName}
                      </p>
                      <p>
                        {
                          state?.invoice?.invoiceCoverNoteData
                            ?.organisationAddress
                        }
                      </p>
                    </div>
                    <div className="middle-block">
                      <p>
                        ABN :
                        <span>
                          {state?.invoice?.invoiceCoverNoteData?.header?.abn}
                        </span>
                      </p>
                      <p>
                        Phone :
                        <a href="tel:0293175722">
                          {state?.invoice?.invoiceCoverNoteData?.header?.phone}
                        </a>
                      </p>
                    </div>
                    <div className="right-block">
                      <p>
                        Email :
                        <a href="mailto:accounts@wymap.com">
                          {state?.invoice?.invoiceCoverNoteData?.header?.email}
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
                <div className="content-block">
                  <div className="detail-block clearfix">
                    <div className="left-block">
                      <h2>Bill To</h2>
                      <p>
                        {
                          state?.invoice?.invoiceCoverNoteData?.header
                            ?.customerName
                        }
                      </p>
                      <p>{state?.invoice?.invoiceCoverNoteData?.billTo}</p>
                      <p className="purchase-order">
                        {" "}
                        Purchase Order:
                        <span>
                          {state?.invoice?.invoiceCoverNoteData?.header
                            ?.purchaseOrder || "0"}
                        </span>
                      </p>
                    </div>
                    <div className="right-block">
                      <div className="shipping-adress-block clearfix">
                        <div className="inner-col first">
                          <h2>{`${state?.invoice?.invoiceCoverNoteData?.organisationName} EFT Details`}</h2>
                          {/* https://wymap.atlassian.net/browse/MAPTRAK-927 hide BSB if no value found */}
                          {state?.invoice?.invoiceCoverNoteData?.ETFDetails
                            ?.bsb && (
                            <p>
                              BSB :{" "}
                              {
                                state?.invoice?.invoiceCoverNoteData?.ETFDetails
                                  ?.bsb
                              }
                            </p>
                          )}
                          <p>
                            Account Number :{" "}
                            {
                              state?.invoice?.invoiceCoverNoteData?.ETFDetails
                                ?.accountNumber
                            }
                          </p>
                          <p>
                            Account Name :{" "}
                            {
                              state?.invoice?.invoiceCoverNoteData?.ETFDetails
                                ?.accountName
                            }
                          </p>
                          <p>
                            Bank :{" "}
                            {
                              state?.invoice?.invoiceCoverNoteData?.ETFDetails
                                ?.bank
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {showCoverNote()}
                  {state?.invoice?.invoiceCoverNoteData?.MIVTotalNet ? (
                    <div className="gray-bg-title-strip total-invoice clearfix">
                      <h3>Difference due to weekly minimum charge</h3>
                      <div className="right-block">
                        <p>
                          {state?.invoice?.invoiceCoverNoteData?.MIVTotalNet
                            ? formatAustralianCurrency(
                                state?.invoice?.invoiceCoverNoteData
                                  ?.MIVTotalNet
                              )
                            : "-"}
                        </p>
                        <p>
                          {state?.invoice?.invoiceCoverNoteData?.MIVTotalTax
                            ? formatAustralianCurrency(
                                state?.invoice?.invoiceCoverNoteData
                                  ?.MIVTotalTax
                              )
                            : "-"}
                        </p>
                        <p>
                          {state?.invoice?.invoiceCoverNoteData?.MIVTotalGross
                            ? formatAustralianCurrency(
                                state?.invoice?.invoiceCoverNoteData
                                  ?.MIVTotalGross
                              )
                            : "-"}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
                <div className="footer">
                  <p className="querie-link">
                    Please contact
                    <a href="mailto:accounts@wymap.com.au">
                      accounts@wymap.com.au
                    </a>{" "}
                    should you have any invoice queries.
                  </p>
                  <p>
                    “All transactions with Wymap Group are subject to our
                    standard terms and conditions unless varied by written
                    agreement. Terms are available on request or on the relevant
                    business website.”
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <PopupDialog
        open={openPopup}
        onClose={() => setOpenPopup(false)}
        title="Reconciliation Mismatch"
        content="Only Cover Notes with mismatches will be excluded from dispatch. Approval of all other valid invoices will proceed as normal."
        confirmLabel="OK"
        onConfirm={() => setOpenPopup(false)}
        showCloseIcon={false}
      />
    </>
  );
}
export default InvoiceCoverNote;
